import { ProductType } from '@features/purchase/models/product.model';
import { CompanyType } from '@features/purchase/models/company.model';

export enum PaymentType {
  CreditCard,
  ACH,
  Wire,
  Check,
  CSBypass
}

export const PaymentTypeLabels = {
  [PaymentType.CreditCard]: 'Credit Card',
  [PaymentType.ACH]: 'ACH',
  [PaymentType.Wire]: 'Wire',
  [PaymentType.Check]: 'Check',
  [PaymentType.CSBypass]: 'CS Bypass',
}

export interface PurchaseInformation {
  productType: ProductType;
  subscriptionTierId: string;
  licenseSeats: number;
  quantity?: number;
}

export interface BundleInformation {
  subscriptionTierId?: string;
  licenseSeats: number;
}

export interface ContactInformation {
  firstName: string;
  lastName: string;
  email: string;
  jobTitle: string;
  phoneNumber: string;
}

export interface CompanyInformation {
  companyId: string | null;
  userId: string | null;
  companyType: CompanyType;
  memberId: string | null;
  companyName: string;
  country: string;
  postalCode: string;
  city: string;
  region: string;
  address1: string;
  agreementId: string | null;
  hasShipInformation?: boolean;
}

export interface ShippingInformation {
  firstName: string;
  lastName: string;
  address1: string;
  postalCode: string;
  city: string;
  region: string;
  country: string;
}

export interface OrderPayment {
  paymentInfo: {
    clientReferenceInformationCode: string | null; // leave blank
    paymentType: PaymentType;
    totalAmount: string;
    discountAmount: string;
    currency: string;
    contact: {
      firstName: string;
      lastName: string;
      address1: string;
      city: string;
      state: string;
      postalCode: string;
      country: string;
      email: string;
      phone: string;
    };
    creditCard: {
      cardNumber: string;
      expirationMonth: string;
      expirationYear: string;
      cvv: string;
    };
    bankAccount: {
      accountType: string;
      accountOwner: string;
      accountNumber: string;
      routingNumber: string;
    };
    check: {
      checkNumber: string;
      wireIdentifier: string;
      originatingPartyName: string;
    };
  };
  companyId?: string | null;
  userId?: string | null;
  glCode: string;
  productName: string;
  purchaseShortDescription: string | null; // leave blank
}

export interface BasicPaymentInfo {
  productName: string;
  paymentId: string;
  licenses?: number;
  quantity?: number;
  userEmail?: string;
  forAdditionalLicenses?: boolean;
}

export const PAYMENT_INFO_INITIAL_STATE: BasicPaymentInfo = {
  productName: '',
  paymentId: '',
  licenses: undefined,
  userEmail: undefined,
  forAdditionalLicenses: false
}

export enum PurchaseResponseCodes {
  Incomplete,
  Complete,
  UnknownIssue,
  MissingRequestProperties,
  CompanyRetrieveIssue,
  CompanySaveIssue,
  UserRetrieveIssue,
  UserSaveIssue,
  AssignToAdminIssue,
  PaymentIssue,
  PaymentSavedIssue,
  SubscriptionSaveIssue,
  SubscriptionUserSaveIssue,
  CannotGetProductIssue
}

interface BasicOrder {
  purchaseInformation: PurchaseInformation;
  paymentPayload: OrderPayment;
}

export interface Order extends BasicOrder {
  bundleInformation: BundleInformation;
  contactInformation: ContactInformation;
  companyInformation: CompanyInformation;
  shippingInformation: ShippingInformation | null;
  purchaseMode: PurchaseMode;
  csWithAssociatedCompanyPurchase: boolean;
  isForExistingCompany?: boolean;
}

export interface AddLicenseOrder extends BasicOrder {
  userId?: string;
  companyId?: string;
}

export enum PurchaseMode {
  PURCHASE,
  RENEWAL,
  WEB_LICENSE_ADD,
  UNKNOWN
}

export type OrderState = Order;

export const ORDER_INITIAL_STATE: OrderState = {
  purchaseInformation: {
    productType: ProductType.NONE,
    subscriptionTierId: '',
    licenseSeats: 1,
    quantity: 1
  },
  bundleInformation: {
    licenseSeats: 0,
  },
  contactInformation: {
    firstName: '',
    lastName: '',
    email: '',
    jobTitle: '',
    phoneNumber: '',
  },
  companyInformation: {
    companyId: null,
    userId: null,
    companyType: CompanyType.NONE,
    memberId: null,
    companyName: '',
    country: '',
    postalCode: '',
    city: '',
    region: '',
    address1: '',
    agreementId: null
  },
  shippingInformation: null,
  paymentPayload: {
    paymentInfo: {
      clientReferenceInformationCode: null,
      paymentType: PaymentType.CreditCard,
      totalAmount: '',
      discountAmount: '',
      currency: '',
      contact: {
        firstName: '',
        lastName: '',
        address1: '',
        city: '',
        state: '',
        postalCode: '',
        country: '',
        email: '',
        phone: '',
      },
      creditCard: {
        cardNumber: '',
        expirationMonth: '',
        expirationYear: '',
        cvv: '',
      },
      bankAccount: {
        accountType: '',
        accountOwner: '',
        accountNumber: '',
        routingNumber: '',
      },
      check: {
        checkNumber: '',
        wireIdentifier: '',
        originatingPartyName: '',
      },
    },
    companyId: null,
    userId: null,
    glCode: '',
    productName: '',
    purchaseShortDescription: null
  },
  purchaseMode: PurchaseMode.PURCHASE,
  csWithAssociatedCompanyPurchase: false,
};
